import { IAddress } from '@pepperhq/types-sdk';
import {
    IEnrichedCategory,
    IEnrichedMenuWithModifierMaps,
    IEnrichedProduct,
    isProductGroup
} from 'components/menu/model/Menu';
import { IOrderCreatePaymentV8, OrderAdjustmentType, OrderScenario } from 'components/order/model/Order';
import { Timeslot } from 'components/timeslots/model/Timeslot';
import {
    isArrayOf,
    isBoolean,
    isDefined,
    isNumber,
    isOptional,
    isOptionalArrayOf,
    isOptionalString,
    isString
} from 'lib/typeInference';
import {
    IOrderPaymentReadResource,
    IAdjustmentReadResourceV10 as IAdjustmentResource
} from '../../order/model/Order';

interface IBasketDelivery {
    dropoff: IAddress;
    quoteId?: string;
}

export interface IBasketRequestResource {
    scenario: OrderScenario;
    locationId: string;
    items: IBasketItem[];
    adjustments?: IBasketAdjustment[];
    deliveryLocation?: string;
    payments?: IOrderCreatePaymentV8[];
    timeSlot?: Timeslot;
    delivery?: IBasketDelivery;
}

export interface IBasketItemV10 {
    productId: string;
    categoryId: string;
    quantity: number;
    modifiers?: IBasketItemModifier[];
    referenceId?: string;
    parentReferenceId?: string;
    note?: string;
}

export interface IBasketAdjustment {
    awardId?: string;
    quantity?: number;
    value?: number;
    type?: OrderAdjustmentType;
    isPercentage?: boolean;
    couponCode?: string;
    description?: string;
}

export interface IBasketItem extends IBasketItemV10 {
    adjustments?: IBasketAdjustment[];
}

export interface IBasketItemModifier {
    id: string;
    options: IBasketItemModifierOption[];
}

export interface IBasketItemModifierOption {
    id: string;
    name?: string;
    cost?: number;
}

// ------------------------------------------------------------------
// V10 RESPONSE Resource Types
// ------------------------------------------------------------------

export type CurrencyCode = string;

export interface DeliveryQuote {
    id: string;
    expiresAt?: Date;
}

interface IBasketDeliveryResource {
    dropoffAddress: IAddress;
    quote: DeliveryQuote;
    eta?: {
        dropoff: Date;
        dropoffDeadline: Date;
        pickup: Date;
    };
}
// use common resources in V10
export interface IBasketResourceV10 {
    locationId: string;
    currencyCode: CurrencyCode;
    scenario: OrderScenario;
    items: IItemReadResourceV10[];
    adjustments: IAdjustmentResource[];
    availableAdjustments: IAdjustmentResource[];
    total: IBasketTotal;
    payments?: IOrderPaymentReadResource[];
    delivery?: IBasketDeliveryResource;
}

export interface IItemModifierOptionResourceV10 {
    id: string;
    name: string;
    cost: number;
    title?: string;
    price?: number;
}

function isItemModifierOptionResourceV10(input: any): input is IItemModifierOptionResourceV10 {
    return isString(input.id) && isString(input.name) && isNumber(input.cost);
}

export interface IItemModifierResourceV10 {
    id: string;
    name?: string;
    title?: string;
    options: IItemModifierOptionResourceV10[];
}

function isItemModifierResourceV10(input: any): input is IItemModifierOptionResourceV10 {
    return (
        isString(input.id) &&
        isString(input.name) &&
        isArrayOf(isItemModifierOptionResourceV10, input.options)
    );
}

export interface IAdjustmentReadResourceV22 {
    id?: number;
    userId?: string;
    awardId?: string;
    parentId?: number;
    paymentId?: number;
    pointsRedeemed?: number;
    description?: string;
    isAllocation?: boolean;
    quantity?: number;
    type?: OrderAdjustmentType;
    value: number;
    deleted?: boolean;
    isPercentage?: boolean;
    taxToAdd?: number;
    couponCode?: string;
    charges?: number;
}

function isAdjustmentReadResourceV10(input: any): input is IAdjustmentReadResourceV22 {
    return (
        isNumber(input.id) &&
        isOptional(isString, input.userId) &&
        isOptionalString(input.awardId) &&
        isOptional(isNumber, input.parentId) &&
        isOptional(isNumber, input.paymentId) &&
        isOptional(isNumber, input.pointsRedeemed) &&
        isOptionalString(input.description) &&
        isBoolean(input.isAllocation) &&
        isNumber(input.quantity) &&
        input.type in OrderAdjustmentType &&
        isNumber(input.value) &&
        isOptional(isBoolean, input.deleted)
    );
}

// Should be updated to v20
export interface IItemReadResourceV10 {
    id: number;
    userId: string;
    categoryId: string;
    categoryName: string;
    productId: string;
    productName: string;
    modifiers?: IItemModifierResourceV10[];
    adjustments?: IAdjustmentReadResourceV22[];
    cost: number;
    quantity: number;
    deleted?: boolean;
    referenceId?: string;
    parentReferenceId?: string;
    note?: string;
    // Computed at runtime
    isClaimedBy?: string[];
    _uiKey?: string;
    children?: IItemReadResourceV10[];
    isNested?: boolean;
}

export function isItemReadResourceV10(input: any): input is IItemReadResourceV10 {
    return (
        isNumber(input.id) &&
        isString(input.userId) &&
        isString(input.categoryId) &&
        isString(input.categoryName) &&
        isString(input.productId) &&
        isString(input.productName) &&
        isOptionalArrayOf(isItemModifierResourceV10, input.modifiers) &&
        isOptionalArrayOf(isAdjustmentReadResourceV10, input.adjustments) &&
        isNumber(input.cost) &&
        isNumber(input.quantity) &&
        isOptional(isBoolean, input.deleted) &&
        isOptionalString(input.referenceId) &&
        isOptionalString(input.parentReferenceId)
    );
}

export interface ITaxLine {
    name: string;
    id: string;
    amount: number;
    inclusive: boolean; // true if tax is included in the item cost
    rate: number; // tax rate as a fraction 0..1
}

function isTaxLine(input: any): input is ITaxLine {
    return (
        isString(input.name) &&
        isString(input.id) &&
        isNumber(input.amount) &&
        isBoolean(input.inclusive) &&
        isNumber(input.rate)
    );
}

export interface IBasketTotal {
    cost: number;
    charges: number;
    discounts: number;
    taxes: ITaxLine[];
    total: number; // total including exclusive tax.  this is the payment amount excluding tips
    balance: number;
    payments: number;
}

export function isBasketTotal(input: any): input is IBasketTotal {
    return (
        isNumber(input.cost) &&
        isNumber(input.charges) &&
        isNumber(input.discounts) &&
        isArrayOf(isTaxLine, input.taxes) &&
        isNumber(input.total)
    );
}

export function isBasketCalculatorData(input: any): input is IBasketResourceV10 {
    return (
        isString(input.locationId) &&
        isString(input.currencyCode) &&
        input.scenario in OrderScenario &&
        isArrayOf(isItemReadResourceV10, input.items) &&
        isArrayOf(isAdjustmentReadResourceV10, input.adjustments) &&
        isArrayOf(isAdjustmentReadResourceV10, input.availableAdjustments) &&
        isBasketTotal(input.total)
    );
}

// Item option modifier validation function
export function isOptionModifiersValid(menu: IEnrichedMenuWithModifierMaps, categoryId: string) {
    return (item: IBasketItemModifier) => {
        const modifier =
            menu.modifierIdToCategoryOptionModifierSettings.get(categoryId)?.get(item.id) ??
            menu.modifierIdToOptionModifierSettings.get(item.id);
        if (!modifier) {
            return false;
        }
        if (item.options.length < modifier.min || item.options.length > modifier.max) {
            return false;
        }
        return item.options.every(option =>
            modifier.options.some(
                currentOption =>
                    currentOption.id === option.id &&
                    (currentOption.available || !isDefined(currentOption.available))
            )
        );
    };
}

// Item product modifier validation function
export function isProductModifierValid(
    item: IBasketItem | IItemReadResourceV10,
    products: IEnrichedProduct[],
    items: IBasketItem[] | IItemReadResourceV10[]
) {
    let productId: string | undefined;
    if (item.parentReferenceId) {
        productId = items.find(basketItem => basketItem.referenceId === item.parentReferenceId)?.productId;
    }
    if (item.parentReferenceId && !productId) {
        return false;
    }
    const relatedProduct = products.find(product => product.id === productId);
    if (relatedProduct && isDefined(relatedProduct.available) && !relatedProduct.available) {
        return false;
    }
    return true;
}

function isBasketItemValid(
    products: IEnrichedProduct[],
    items: IBasketItem[],
    menu: IEnrichedMenuWithModifierMaps,
    nestedModifiersEnabled: boolean
) {
    return (item: IBasketItem) => {
        let product;
        if (item.categoryId) {
            const category = menu.categories.find(category => category.id === item.categoryId);
            product = category?.products?.find(product => product.id === item.productId);
        }
        if (!product || (isDefined(product.available) && !product.available)) {
            return false;
        }
        const productModifierValid = isProductModifierValid(item, products, items);
        const productModifierCountValid = isProductModifierCountValid(
            item,
            items,
            menu,
            nestedModifiersEnabled
        );
        const optionsModifierValid =
            !item.modifiers ||
            !item.modifiers.length ||
            item.modifiers.every(isOptionModifiersValid(menu, item.categoryId));
        return productModifierValid && productModifierCountValid && optionsModifierValid;
    };
}

export function filterBasket(
    categories: IEnrichedCategory[],
    items: IBasketItem[],
    menu?: IEnrichedMenuWithModifierMaps,
    nestedModifiersEnabled = false
) {
    if (!menu) {
        return { items, isValid: true };
    }
    const products = categories.reduce<IEnrichedProduct[]>((acc, item) => {
        item.products.forEach(product => {
            if (isProductGroup(product)) {
                product.products.forEach(innerProduct => {
                    acc.push(innerProduct);
                });
            } else {
                acc.push(product);
            }
        });
        return acc;
    }, []);
    const newItems = items.filter(isBasketItemValid(products, items, menu, nestedModifiersEnabled));
    const isValid = newItems.length === items.length;
    return { isValid, items: isValid ? items : newItems };
}

type HasReferenceIds = Pick<IBasketItem, 'parentReferenceId' | 'referenceId'>;

// Recursively get all basket items for a root reference id and put them in a flat array
export function getAllModifierProductsForRootReferenceId<T extends HasReferenceIds>(
    basketItems: T[],
    referenceId: string
): T[] {
    const modifierProducts: T[] = [];
    for (const item of basketItems) {
        if (isDefined(item.parentReferenceId) && item.parentReferenceId === referenceId) {
            modifierProducts.push(item);
            if (isDefined(item.referenceId)) {
                modifierProducts.push(
                    ...getAllModifierProductsForRootReferenceId(basketItems, item.referenceId)
                );
            }
        }
    }
    return modifierProducts;
}

type HasProductModifierFields = HasReferenceIds & Pick<IBasketItem, 'productId' | 'quantity'>;

// Modifier product quantities are stored pre-multiplied by their root item's quantity (see
// basketSlice's updateQuantity and getModifierProductsString), so a selection count is only
// meaningful relative to that root quantity - at every level of nesting.
function getRootItemQuantity<T extends HasProductModifierFields>(item: T, items: T[]): number {
    let node: T | undefined = item;
    while (node && isDefined(node.parentReferenceId)) {
        const currentNode: T = node;
        node = items.find(candidate => candidate.referenceId === currentNode.parentReferenceId);
    }
    return isDefined(node) && node.quantity > 0 ? node.quantity : 1;
}

// Item product modifier count validation function - checks selected product-modifier
// quantities against the modifier's current min/max by walking up the parentReferenceId chain,
// so a child item is invalidated along with its parent rather than left orphaned.
export function isProductModifierCountValid<T extends HasProductModifierFields>(
    item: T,
    items: T[],
    menu: IEnrichedMenuWithModifierMaps,
    nestedModifiersEnabled = false
): boolean {
    const rootQuantity = getRootItemQuantity(item, items);
    let node: T | undefined = item;
    while (node) {
        const currentNode: T = node;
        const isNestedLevel = isDefined(currentNode.parentReferenceId);
        // With nested modifiers disabled the product info screen only ever renders a root
        // product's own modifiers, so a modifier product's own requirements can never be
        // satisfied. Enforcing them here would drop items the user had no way of completing.
        if (nestedModifiersEnabled || !isNestedLevel) {
            const product = menu.productIdToProduct.get(currentNode.productId);
            const children = isDefined(currentNode.referenceId)
                ? items.filter(sibling => sibling.parentReferenceId === currentNode.referenceId)
                : [];
            for (const modifierId of product?.modifiers ?? []) {
                const settings = menu.modifierIdToProductModifierSettings.get(modifierId);
                if (!settings) {
                    continue;
                }
                const validProductIds = menu.modifierIdToProductIds.get(modifierId) ?? [];
                // Sum quantities rather than counting lines, since selecting the same modifier
                // product twice is stored as one item with quantity 2. Scaling min/max by the root
                // quantity instead of dividing keeps this to integer maths, so `2x cheese` on a
                // `quantity: 2` burger (stored as quantity 4) still satisfies a min/max of 2.
                const selectedQuantity = children.reduce(
                    (total, child) =>
                        validProductIds.includes(child.productId) ? total + child.quantity : total,
                    0
                );
                if (
                    selectedQuantity < settings.min * rootQuantity ||
                    selectedQuantity > settings.max * rootQuantity
                ) {
                    return false;
                }
            }
        }
        node = isNestedLevel
            ? items.find(candidate => candidate.referenceId === currentNode.parentReferenceId)
            : undefined;
    }
    return true;
}

export interface PayBasketItemResource {
    id: string;
    userId: string;
    categoryId: string;
    categoryName: string;
    productId: string;
    productName: string;
    modifiers?: IItemModifierResourceV10[];
    adjustments?: IAdjustmentReadResourceV22[];
    cost: number;
    quantity: number;
    deleted?: boolean;
    referenceId?: string;
    parentReferenceId?: string;
    note?: string;
    // Computed at runtime
    isClaimedBy?: string[];
}
